import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { useShortcutContext } from '@/contexts/ShortcutContext'

function KeyBadge({ keyStr }: { keyStr: string }) {
  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)
  const display = keyStr
    .replace(/Ctrl\+/gi, isMac ? '⌘' : 'Ctrl+')
    .replace(/Alt\+/gi, isMac ? '⌥' : 'Alt+')
    .replace(/Shift\+/gi, isMac ? '⇧' : 'Shift+')
  return (
    <kbd className="inline-flex items-center px-2 py-0.5 rounded border border-border bg-muted text-xs font-mono text-foreground dark:bg-muted dark:border-border dark:text-foreground">
      {display}
    </kbd>
  )
}

export function ShortcutHelpOverlay() {
  const { shortcuts, showHelp, toggleHelp } = useShortcutContext()
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showHelp) return
    const handler = (e: MouseEvent) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) {
        toggleHelp()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showHelp, toggleHelp])

  if (!showHelp) return null

  const grouped = new Map<string, typeof shortcuts>()
  for (const s of shortcuts) {
    const list = grouped.get(s.category) || []
    list.push(s)
    grouped.set(s.category, list)
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
      <div
        ref={overlayRef}
        className="bg-background dark:bg-card rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border dark:border-border">
          <h2 className="text-lg font-semibold text-foreground dark:text-white">Atajos de teclado</h2>
          <button
            onClick={toggleHelp}
            className="p-1 rounded-md hover:bg-muted dark:hover:bg-accent text-muted-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-6">
          {Array.from(grouped.entries()).map(([category, items]) => (
            <div key={category}>
              <h3 className="text-xs font-semibold uppercase text-muted-foreground dark:text-muted-foreground mb-2 tracking-wider">
                {category}
              </h3>
              <div className="space-y-1.5">
                {items.map((s) => (
                  <div key={s.key} className="flex items-center justify-between py-1">
                    <span className="text-sm text-foreground dark:text-muted-foreground">{s.label}</span>
                    <KeyBadge keyStr={s.key} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-border dark:border-border text-center">
          <span className="text-xs text-muted-foreground">Presiona <KeyBadge keyStr="?" /> o <KeyBadge keyStr="Escape" /> para cerrar</span>
        </div>
      </div>
    </div>
  )
}
